"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown, X } from "lucide-react";
import { track } from "@lib/track";

interface Route {
  name: string;
  link: string;
  secondaryLinks?: { name: string; link: string }[];
}

interface Props {
  routes: Route[];
}

const HamburgerMenu: React.FC<Props> = ({ routes }) => {
  const [open, setOpen]         = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Defer pathname to client-only to avoid hydration mismatch
  const [activePath, setActivePath] = useState<string>("");
  // Defer year to client-only
  const [year, setYear] = useState<number | null>(null);

  const drawerRef  = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pathname   = usePathname();

  // Sync activePath after mount — never runs on server
  useEffect(() => {
    setActivePath(pathname ?? "");
  }, [pathname]);

  // Set year client-side only
  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  const close = () => {
    setOpen(false);
    setExpanded(null);
  };

  // Close on route change
  useEffect(() => {
    close();
  }, [pathname]);

  // Body scroll lock + ESC
  useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open]);

  // Focus trap
  useEffect(() => {
    if (!open || !drawerRef.current) return;
    const drawer    = drawerRef.current;
    const focusable = drawer.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    const trap  = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
    drawer.addEventListener("keydown", trap);
    return () => drawer.removeEventListener("keydown", trap);
  }, [open]);

  const onPrimaryClick = (r: Route) => {
    track("nav_click",
      { label: r.name, component: "MobileMenu" },
      { event: "nav_click", label: r.name, component: "MobileMenu", source: "web" }
    );
  };

  const onSecondaryClick = (parent: Route, s: { name: string; link: string }) => {
    track("nav_click",
      { label: `${parent.name} > ${s.name}`, component: "MobileMenu" },
      { event: "nav_click", label: `${parent.name} > ${s.name}`, component: "MobileMenu", source: "web" }
    );
  };

  return (
    <div className="lg:hidden">
      {/* Trigger */}
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="mobile-nav-drawer"
        aria-label={open ? "Close menu" : "Open menu"}
        className="
          flex flex-col justify-center gap-[5px] w-10 h-10
          text-[var(--text-on-dark)]
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] rounded-md
        "
      >
        <span className={`block w-6 h-[1.5px] bg-current transition-all duration-200 origin-center ${open ? "rotate-45 translate-y-[6.5px]" : ""}`} />
        <span className={`block w-6 h-[1.5px] bg-current transition-all duration-200 ${open ? "opacity-0 scale-x-0" : ""}`} />
        <span className={`block w-6 h-[1.5px] bg-current transition-all duration-200 origin-center ${open ? "-rotate-45 -translate-y-[6.5px]" : ""}`} />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          aria-hidden="true"
          onClick={close}
        />
      )}

      {/* Slide-in drawer */}
      <div
        id="mobile-nav-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        className={`
          fixed top-0 left-0 h-full w-[82vw] max-w-[320px] z-50
          bg-[var(--bg-surface)]
          flex flex-col
          transition-transform duration-300 ease-in-out
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Drawer header — close button only */}
        <div
          className="flex items-center justify-end px-4 py-3"
          style={{ borderBottom: "1px solid var(--border-on-light)" }}
        >
          <button
            onClick={close}
            aria-label="Close menu"
            className="
              p-2 rounded-full
              text-[var(--text-on-light-muted)] hover:text-[var(--text-brand)]
              hover:bg-[var(--bg-surface-dk)] transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
            "
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Nav links */}
        <nav aria-label="Mobile navigation" className="flex-1 overflow-y-auto px-4 py-3">
          <ul>
            {routes.map((route, i) => {
              const hasChildren = !!route.secondaryLinks?.length;
              const isExpanded  = expanded === route.name;
              // Use activePath (client-only) — empty string on server so classes are stable
              const isActive    = activePath !== "" && (
                activePath === route.link || activePath.startsWith(route.link + "/")
              );

              return (
                <li
                  key={route.name}
                  className="border-b border-[var(--border-on-light)] last:border-none"
                  style={{ transitionDelay: `${i * 35}ms` }}
                >
                  <div className="flex items-center">
                    <Link
                      href={route.link}
                      prefetch={false}
                      onClick={() => onPrimaryClick(route)}
                      className={`
                        flex-1 py-4 text-xl tracking-tight leading-snug transition-colors
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] rounded
                        ${isActive
                          ? "text-[var(--text-brand)] font-semibold"
                          : "text-[var(--text-on-light)] hover:text-[var(--text-brand)]"
                        }
                      `}
                    >
                      {route.name}
                    </Link>

                    {hasChildren && (
                      <button
                        onClick={() => setExpanded((prev) => (prev === route.name ? null : route.name))}
                        aria-expanded={isExpanded}
                        aria-controls={`submenu-${route.name}`}
                        aria-label={`${isExpanded ? "Collapse" : "Expand"} ${route.name}`}
                        className="
                          ml-2 p-2 rounded-full shrink-0
                          text-[var(--text-on-light-muted)] hover:text-[var(--text-brand)]
                          hover:bg-[var(--bg-surface-dk)] transition-colors
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
                        "
                      >
                        <ChevronDown
                          className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                          aria-hidden="true"
                        />
                      </button>
                    )}
                  </div>

                  {/* Sub-links */}
                  {hasChildren && (
                    <ul
                      id={`submenu-${route.name}`}
                      aria-hidden={!isExpanded}
                      className={`
                        overflow-hidden transition-all duration-200
                        ${isExpanded ? "max-h-[400px] pb-3" : "max-h-0"}
                      `}
                    >
                      {route.secondaryLinks!.map((s) => {
                        const isSubActive = activePath !== "" && activePath === s.link;
                        return (
                          <li key={s.name}>
                            <Link
                              href={s.link}
                              prefetch={false}
                              tabIndex={isExpanded ? 0 : -1}
                              onClick={() => onSecondaryClick(route, s)}
                              className={`
                                block py-2.5 pl-4 text-base rounded-lg transition-colors
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
                                ${isSubActive
                                  ? "text-[var(--text-brand)] font-medium bg-[var(--bg-card)]"
                                  : "text-[var(--text-on-light-muted)] hover:text-[var(--text-brand)] hover:bg-[var(--bg-surface-dk)]"
                                }
                              `}
                            >
                              {s.name}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom CTA */}
        <div className="px-4 pb-8 pt-3 border-t border-[var(--border-on-light)]">
          <Link
            href="/workwithme/discoverycall"
            prefetch={false}
            onClick={() =>
              track("nav_click",
                { label: "CTA: Free Discovery Call", component: "MobileMenu" },
                { event: "nav_click", label: "CTA: Free Discovery Call", component: "MobileMenu", source: "web" }
              )
            }
            className="
              block w-full text-center px-4 py-3.5 rounded-xl text-sm font-medium
              bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)]
              hover:bg-[var(--btn-primary-hover)] transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]
            "
          >
            Book a free discovery call
          </Link>
          {/* suppressHydrationWarning prevents year mismatch between server and client */}
          <p
            suppressHydrationWarning
            className="mt-4 text-center text-xs tracking-[0.16em] uppercase text-[var(--text-on-light-muted)]"
          >
            © {year ?? ""} cforganicwellness.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default HamburgerMenu;
